import React, { useEffect, useState, useRef, useCallback } from "react";
import { currencyFormatter } from "@utils";
import { connect } from "react-redux";
import {
  deleteSellCardInCart,
  sendCardAndPinToServer
} from "@actions/sellActions";
import { defaultAction } from "@actions/defaultAction";
import {
  CLEAR_CARD_AND_PIN_ENTRY,
  CLEAR_UPDATE_CARD_SERVER_ERROR,
  UPDATE_CARD_NUMBER,
  UPDATE_CARD_PIN
} from "@constants/actionTypes";
import styled from "@emotion/styled";
import DeleteCardTrashCan from "@components/DeleteCardTrashCan";
import { ClearedStyleInput } from "@modules/Components";
import { useFormHook } from "@utils/hooks/formHook";
import { validateSingleItem } from "@utils/validators/form";
import { FaTimes } from "react-icons/fa";
import { debounce } from "lodash";

const ContainerGrid = styled.div`
  display: grid;
  grid-template-columns: 191px 330px 155px 95px;
  grid-gap: 20px;
  width: 92%;
  margin: 0 auto;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: 1150px) {
    grid-template-columns: 1fr 295px 150px 120px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 102px 268px 135px 121px;
  }
  @media (max-width: 800px) {
    display: block;
    width: 100%;
    border-bottom: 1px solid rgba(119, 121, 132, 0.15);
    margin-bottom: 0px;
    padding: 30px 5% 10px 5%;
  }
`;

const CardInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  margin-right: 15px;
  align-items: center;

  @media (max-width: 900px) {
    margin-right: 0px;
  }
  @media (max-width: 800px) {
    margin-bottom: 25px;
    grid-gap: 10px;
  }
`;

const InputContainer = styled.div`
  border-radius: 5px;
  height: 100%;
  width: 100%;
`;

const InfoText = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #777984;
`;

const InfoName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const InfoAmount = styled.div`
  font-weight: 500;
`;

const ImageContainer = styled.div`
  @media (max-width: 1150px) {
    display: none;
  }
  @media (max-width: 800px) {
    display: initial;
  }
`;

const Image = styled.img`
  opacity: ${props => props.setOpacity};
  align-self: center;
  position: absolute;
  left: 15px;
  pointer-events: none;
`;

const LockImage = styled(Image)`
  width: 12px;
  height: auto;
`;

const CardImage = styled(Image)`
  width: 17px;
  height: auto;
`;

const InputDiv = styled.div`
  width: 100%;
  margin: 0 auto;
  border-radius: 5px;
  box-shadow: 0 20px 70px 0 rgba(34, 35, 36, 0.12);
  background-color: #ffffff;
  display: grid;
  grid-template-columns: 0.4fr 2fr;
  align-items: center;
  padding: 0;
  height: 72px;
  font-size: 16px;
  position: relative;
  grid-template-columns: 1fr;

  @media (max-width: 800px) {
    margin-bottom: 20px;
  }
`;

const InputField = styled(ClearedStyleInput)`
  border: ${props => (props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "")};
  color: rgba(31, 32, 33, 0.65);
  opacity: 1;
  padding-left: 40px;
  height: 100%;
  width: 100%;
  border-radius: 5px;

  &::placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &::-webkit-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &:-ms-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }

  &:focus {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "2px solid black"};
  }
  &:hover {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : ""};
  }
`;

const ErrorMessageInner = styled.div`
  color: #ee5050;
  font-size: 10px;
  position: absolute;
  font-weight: 500;
  line-height: 1.2;
  top: 3px;
  left: 15px;
  background: rgba(255, 255, 255, 0.75);
  margin-right: 1px;
  padding-right: 14px;
`;

const ErrorMessageInnerUpper = styled(ErrorMessageInner)`
  top: -19px;
`;

const YourOffer = styled.div`
  margin-left: 15px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const OfferTextEmoji = styled.div`
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1px;
`;

const OfferText = styled.span`
  color: rgba(31, 32, 33, 0.5);
  text-transform: uppercase;
`;

const OfferDollarSpan = styled.span`
  font-weight: normal;
  color: rgba(31, 32, 33, 0.5);
`;

const DeleteSpan = styled.span`
  @media (min-width: 1151px) {
    display: none;
  }

  cursor: pointer;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-shadow: 0 6px 12px 0 rgba(31, 32, 33, 0.1);
  background-color: #ffffff;
  display: inline-block;
  margin-left: 5px;
`;

const Xspan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const OfferAmount = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #1f2021;
`;

const CardNumber = styled.div`
  @media (min-width: 801px) {
    display: none;
  }

  width: 95%;
  font-size: 12px;
  font-weight: 500;
  color: #1f2021;
  margin-bottom: 13px;

  @media (max-width: 710px) {
    width: 90%;
  }
`;

const MobileOfferSpan = styled.span`
  @media (min-width: 801px) {
    display: none;
  }
  color: rgba(119, 121, 132, 0.64);
`;

const StepTwoCart = ({
  cardID,
  updateCardNum,
  updatePin,
  cards,
  clearUpdateCardServerError,
  clearCardAndPin,
  sendCardAndPinToServer,
  tradeBonus,
  merchantsSell,
  deleteSellCardInCart,
  changeDisabledArray,
  index,
  setLoading,
  numAndPinMismatch,
  changeNumAndPinMismatch,
  disabledArray,
  submitAttempted,
  changeStateDisabledArray,
  disabledArrayItem,
  setLoadingInfo,
  deleteCardRunning
}) => {
  if (!merchantsSell[cards[cardID].id]) {
    return null;
  }
  const [numberFocus, changeNumberFocus] = useState(false);
  const [pinFocus, changePinFocus] = useState(false);
  const [focusSet, changeFocusSet] = useState(false);
  const [didFocusOnPin, setDidFocusOnPin] = useState(false);
  const [didBlurOnPin, setDidBlurOnPin] = useState(false);
  const inputRef = useRef(null);
  const {
    cardNumEnter,
    pinNumEnter,
    cashValue,
    enterValue,
    id: merchantId
  } = cards[cardID];

  const cardNumFromServer = cards[cardID].number || null;
  const pinNumFromServer = cards[cardID].pin || null;

  const initialValidation =
    merchantsSell[cards[cardID].id].validation === "null:null" ||
    cardNumFromServer
      ? false
      : true;

  const initialState = {
    number: cardNumFromServer || "",
    pin: pinNumFromServer || ""
  };

  const numberValidate = inputObject => {
    const validation = merchantsSell[cards[cardID].id].validation;
    const number = inputObject.number?.trim() || "";
    const pin = inputObject.pin?.trim() || "";

    let isInvalid = false;
    const inputLengthString =
      number.length > 0 ? number.length.toString() : "null";
    const pinLengthString = pin.length > 0 ? pin.length.toString() : "null";

    if (validation && validation.includes(",")) {
      const splitArray = validation.split(",");
      const numbersArray = splitArray.map(num => num.split(":")[0].trim());
      if (numbersArray.includes(inputLengthString)) {
        isInvalid = false;

        const findPin = splitArray
          .filter(item => item.split(":")[0].trim() === inputLengthString)
          .find(
            item =>
              item.split(":")[1].trim() === pinLengthString ||
              item.split(":")[1].trim() === "null"
          );
        if (!findPin) {
          if (
            splitArray
              .map(num => num.split(":")[1].trim())
              .includes(pinLengthString)
          ) {
            changeNumAndPinMismatch({ ...numAndPinMismatch, [cardID]: true });
          }
        } else {
          changeNumAndPinMismatch({ ...numAndPinMismatch, [cardID]: false });
        }
      } else {
        isInvalid = true;
      }
    } else {
      const validateNumber = validation.split(":")[0];
      if (
        validateNumber !== "null" &&
        number.length !== Number(validateNumber)
      ) {
        isInvalid = true;
      }
    }

    if (isInvalid) {
      return "Invalid Card Number";
    }
  };

  const pinValidate = inputObject => {
    const validation = merchantsSell[cards[cardID].id].validation;
    let isInvalid = false;
    const number = inputObject.number?.trim() || "";
    const pin = inputObject.pin?.trim() || "";
    const inputLengthString = pin.length > 0 ? pin.length.toString() : "null";
    const numLengthString =
      number.length > 0 ? number.length.toString() : "null";

    if (validation && validation.includes(",")) {
      const splitArray = validation.split(",");
      const numberExists = splitArray.filter(
        item => item.split(":")[0].trim() === numLengthString
      );
      const thereIsANullValueForThisNumber = numberExists.find(
        item => item.split(":")[1].trim() === "null"
      );

      if (!numberExists.length || thereIsANullValueForThisNumber) {
        return;
      }
      const pinArray = splitArray.map(num => num.split(":")[1].trim());
      if (pinArray.includes(inputLengthString)) {
        isInvalid = false;
        const findNum = splitArray
          .filter(item => item.split(":")[1].trim() === inputLengthString)
          .find(item => item.split(":")[0].trim() === numLengthString);
        if (!findNum) {
          if (
            splitArray
              .map(num => num.split(":")[0].trim())
              .includes(numLengthString)
          ) {
            changeNumAndPinMismatch({ ...numAndPinMismatch, [cardID]: true });
          }
        } else {
          changeNumAndPinMismatch({ ...numAndPinMismatch, [cardID]: false });
        }
      } else {
        isInvalid = true;
      }
    } else {
      const validatePin = validation.split(":")[1];
      if (validatePin !== "null" && pin.length !== Number(validatePin)) {
        isInvalid = true;
      }
    }

    if (isInvalid) {
      return "Invalid Pin Number";
    }
  };

  const inputRules = {
    number: numberValidate,
    pin: pinValidate
  };

  const {
    inputs,
    handleInputChange,
    handleBlur,
    handleFocus,
    disabled,
    formErrors,
    handleSubmit,
    meta
  } = useFormHook(
    f => f,
    initialState,
    validateSingleItem,
    "sellForm",
    inputRules,
    initialValidation
  );

  const debouncePut = useCallback(debounce(postCardAndPin, 500), []);

  useEffect(() => {
    const number = inputs.number.trim();
    const pin = inputs.pin.trim();
    if (!disabled && (pin || number) && !numAndPinMismatch[cardID]) {
      if (
        (number && number !== cardNumFromServer) ||
        (pin && pin !== pinNumFromServer)
      ) {
        debouncePut(number, pin, index, disabled);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    changeDisabledArray({ index, disabled });
  }, [cardNumEnter, pinNumEnter, disabled]);

  useEffect(() => {
    changeDisabledArray({ index, disabled });
  }, [cards[cardID].serverError]);

  useEffect(() => {
    if (cardNumFromServer || pinNumFromServer) {
      clearUpdateCardServerError(cardID);
      updateCardNum(cardID, cardNumFromServer);
      updatePin(cardID, pinNumFromServer);
    }
    changeDisabledArray({ index, disabled });
    return () => {
      if (cards[cardID]) {
        clearCardAndPin(cardID);
      }
    };
  }, []);

  useEffect(() => {
    if (disabledArray.length && !focusSet && !inputRef.current.value) {
      const findIfRequired = disabledArray.find(item => item.disabled === true);
      if (findIfRequired && findIfRequired.index === index) {
        inputRef.current.focus();
        changeFocusSet(true);
      }
    }
  }, [disabledArray, disabledArrayItem]);

  async function postCardAndPin(number, pin, index, disabled) {
    setLoading(true);
    const payload = {
      cardID,
      number,
      pin
    };
    await sendCardAndPinToServer(payload);
    changeDisabledArray({ index, disabled });
    setLoading(false);
  }

  const updateMerchNum = async cardNumber => {
    updateCardNum(cardID, cardNumber);
  };

  const updateMerchPin = cardPin => {
    updatePin(cardID, cardPin);
  };

  const escapeSpecialChar = e => {
    var regex = new RegExp("^[a-zA-Z0-9]+$");
    var str = String.fromCharCode(!e.charCode ? e.which : e.charCode);
    if (regex.test(str)) {
      return true;
    }
    e.preventDefault();
  };

  let totalCardVal = cashValue;
  if (tradeBonus) {
    let valueToAdd;
    totalCardVal = Number(totalCardVal);
    valueToAdd = (tradeBonus / 100) * Number(cashValue);
    totalCardVal += valueToAdd;
    totalCardVal = totalCardVal + "";
  }

  const handleChange = (e, updateFunc) => {
    handleInputChange(e);
    updateFunc(e.target.value.trim());
  };

  const handleNameLength = () => {
    const theName = merchantsSell[merchantId].name;
    const firstWord = theName.replace(/ .*/, "");
    if (firstWord.length > 12 && globalThis?.innerWidth > 799) {
      return `${firstWord.slice(0, 12)}...`;
    }
    return theName;
  };

  useEffect(() => {
    if (
      submitAttempted &&
      disabledArray.find(item => item.disabled && item.index === index)
    ) {
      handleSubmit();
    }

    if (submitAttempted && !didFocusOnPin) {
      setDidFocusOnPin(true);
    }
    if (submitAttempted && !didBlurOnPin) {
      setDidBlurOnPin(true);
    }
  }, [submitAttempted]);

  return (
    <ContainerGrid>
      <CardNumber>Card #{index + 1}</CardNumber>
      <CardInfo>
        <InfoText>
          <InfoName>{handleNameLength()}</InfoName>
          <InfoAmount>
            {currencyFormatter(enterValue)}{" "}
            <MobileOfferSpan>
              ({currencyFormatter(totalCardVal)} offer)
            </MobileOfferSpan>
          </InfoAmount>
        </InfoText>
        <ImageContainer>
          <DeleteCardTrashCan
            setClass={"sell-page-delete-trash"}
            img={merchantsSell[merchantId].image}
            handleClick={async () => {
              setLoadingInfo(true);
              changeStateDisabledArray(
                disabledArray
                  .filter(item => item.index !== index)
                  .map(item => ({
                    index: item.index > index ? item.index - 1 : item.index,
                    disabled: item.disabled
                  }))
              );
              if (!deleteCardRunning) {
                await deleteSellCardInCart(cardID);
              }
              setLoadingInfo(false);
            }}
          />
        </ImageContainer>
      </CardInfo>
      <InputDiv>
        <CardImage
          src={
            ((meta.number.touched && meta.number.blurred) ||
              meta.submitAttempted) &&
            formErrors &&
            formErrors.number
              ? "https://cdn.cardcash.com/website/ui/elements/card-error.svg"
              : "https://cdn.cardcash.com/website/ui/elements/card-dark.svg"
          }
          setOpacity={
            numberFocus ||
            (((meta.number.touched && meta.number.blurred) ||
              meta.submitAttempted) &&
              formErrors &&
              formErrors.number)
              ? 1
              : 0.3
          }
          alt="card"
        />
        <InputContainer>
          <InputField
            ref={inputRef}
            error={
              (((meta.number.touched && meta.number.blurred) ||
                meta.submitAttempted) &&
                formErrors &&
                formErrors.number) ||
              cards[cardID].msg
            }
            className="orange-caret"
            type="text"
            name="number"
            placeholder="Card number..."
            value={inputs.number}
            onChange={e => {
              setLoading(true);
              handleChange(e, updateMerchNum);
            }}
            onBlur={e => {
              handleBlur(e);
              changeNumberFocus(false);
            }}
            onKeyPress={escapeSpecialChar}
            maxLength={30}
            onFocus={e => {
              handleFocus(e);
              changeNumberFocus(true);
            }}
          />
        </InputContainer>
        {((((meta.number.touched && meta.number.blurred) ||
          meta.submitAttempted) &&
          formErrors &&
          formErrors.number) ||
          cards[cardID].msg) && (
          <ErrorMessageInner className={"animated fadeIn"}>
            {formErrors?.number?.errorMessage || cards[cardID].msg}
          </ErrorMessageInner>
        )}
        {numAndPinMismatch[cardID] && didFocusOnPin && didBlurOnPin && (
          <ErrorMessageInnerUpper className={"animated fadeIn"}>
            Card and Pin don't match!
          </ErrorMessageInnerUpper>
        )}
      </InputDiv>

      <InputDiv>
        <LockImage
          src={
            didFocusOnPin && didBlurOnPin && formErrors && formErrors.pin
              ? "https://cdn.cardcash.com/website/ui/elements/lock-error2.svg"
              : "https://cdn.cardcash.com/website/ui/elements/lock-dark.svg"
          }
          setOpacity={
            pinFocus ||
            (didFocusOnPin && didBlurOnPin && formErrors && formErrors.pin)
              ? 1
              : 0.3
          }
          alt="icon"
        />
        <InputContainer>
          <InputField
            error={
              didFocusOnPin && didBlurOnPin && formErrors && formErrors.pin
            }
            className="orange-caret"
            type="text"
            name="pin"
            placeholder="Pin..."
            value={inputs.pin}
            onChange={e => {
              setLoading(true);
              handleChange(e, updateMerchPin);
            }}
            onBlur={e => {
              handleBlur(e);
              changePinFocus(false);
              setDidBlurOnPin(true);
            }}
            onFocus={e => {
              handleFocus(e);
              setDidFocusOnPin(true);
              changePinFocus(true);
            }}
            onKeyPress={escapeSpecialChar}
            maxLength={24}
          />
        </InputContainer>
        {didFocusOnPin && didBlurOnPin && formErrors && formErrors.pin && (
          <ErrorMessageInner className={"animated fadeIn"}>
            {formErrors.pin.errorMessage}
          </ErrorMessageInner>
        )}
      </InputDiv>
      <YourOffer>
        <OfferTextEmoji>
          💰 <OfferText>YOU GET</OfferText>
        </OfferTextEmoji>
        <OfferAmount>
          <OfferDollarSpan>$</OfferDollarSpan>
          {currencyFormatter(totalCardVal, "no-symbol")}
          <DeleteSpan
            onClick={() => {
              if (!deleteCardRunning) {
                deleteSellCardInCart(cardID);
              }
            }}
          >
            <Xspan>
              <FaTimes
                style={{
                  color: "rgba(31, 32, 33, 0.5)",
                  fontSize: "10px"
                }}
              />
            </Xspan>
          </DeleteSpan>
        </OfferAmount>
      </YourOffer>
    </ContainerGrid>
  );
};

export default connect(
  state => ({
    cards: state.cards,
    merchantsSell: state.merchantsSell,
    tradeBonus: state.cartSell.tradeBonus,
    deleteCardRunning: state.cartSell.deleteCardRunning
  }),
  dispatch => ({
    sendCardAndPinToServer: payload =>
      dispatch(sendCardAndPinToServer(payload)),
    clearCardAndPin: cardID =>
      dispatch(defaultAction(CLEAR_CARD_AND_PIN_ENTRY, { cardID })),
    clearUpdateCardServerError: cardID =>
      dispatch(defaultAction(CLEAR_UPDATE_CARD_SERVER_ERROR, { cardID })),
    deleteSellCardInCart: cardID => dispatch(deleteSellCardInCart(cardID)),
    updateCardNum: (cardID, num) =>
      dispatch(defaultAction(UPDATE_CARD_NUMBER, { cardID, number: num })),
    updatePin: (cardID, pin, cb) =>
      dispatch(defaultAction(UPDATE_CARD_PIN, { cardID, pin: pin }))
  })
)(StepTwoCart);
